import type { MeasurementPoint, SupplyLink } from "@energyplatform/client";

// Supply topology on screen. The host decides who feeds whom (SupplyTree); this only lays the forest out.

const ROLES = new Set(["main", "source", "storage", "sub", "load"]);
const MEDIA = new Set(["el", "pv", "bess"]);

const UTILITIES = new Set(["water", "gas", "air"]);

/** Points the supply topology is made of (the host's SupplyTree.Carries), to offer them as supplies. */
export function carries(p: MeasurementPoint): boolean {
  return MEDIA.has(p.medium) && ROLES.has(p.role);
}

/**
 * The supply family of a meter (the host's SupplyTree.FamilyOf): "el" for electricity, PV and storage, the medium for
 * water, gas and compressed air; null for conditions and points outside a balance. Supplies stay within a family.
 */
export function familyOf(p: { medium: string; role: string }): string | null {
  if (!ROLES.has(p.role)) {
    return null;
  }
  if (MEDIA.has(p.medium)) {
    return "el";
  }
  return UTILITIES.has(p.medium) ? p.medium : null;
}

export interface Placed {
  id: string;
  depth: number;
  /** Row of the point; parents sit on the row of their first child, so lines run straight across. */
  row: number;
}

export interface Layout {
  placed: Placed[];
  rows: number;
  depth: number;
  /** Normal supply lines (parent → child) and backup lines. */
  edges: { from: string; to: string; backup: boolean }[];
  /** Points whose supply is unknown (several candidates and none set on the point). */
  unassigned: string[];
}

/**
 * Left-to-right tree: grid connections in the first column, what they feed to the right, one row per leaf. Points
 * fed from outside the shown links (e.g. from a connection in another place) start their own tree.
 */
export function layoutSupply(
  links: readonly SupplyLink[],
  order: (a: string, b: string) => number,
): Layout {
  const ids = new Set(links.map((l) => l.pointId));
  const kids = new Map<string, string[]>();
  for (const l of links) {
    if (l.parentId && ids.has(l.parentId)) {
      kids.set(l.parentId, [...(kids.get(l.parentId) ?? []), l.pointId]);
    }
  }
  const roots = links
    .filter((l) => !l.unassigned && !(l.parentId && ids.has(l.parentId)))
    .map((l) => l.pointId)
    .sort(order);
  const placed: Placed[] = [];
  let next = 0;
  let deepest = 0;
  const seen = new Set<string>();
  const walk = (id: string, depth: number): number => {
    seen.add(id);
    deepest = Math.max(deepest, depth);
    const children = (kids.get(id) ?? [])
      .filter((c) => !seen.has(c))
      .sort(order);
    const row =
      children.length === 0
        ? next++
        : Math.min(...children.map((c) => walk(c, depth + 1)));
    placed.push({ id, depth, row });
    return row;
  };
  roots.forEach((r) => walk(r, 0));
  const edges = links.flatMap((l) => [
    ...(l.parentId && seen.has(l.parentId) && seen.has(l.pointId)
      ? [{ from: l.parentId, to: l.pointId, backup: false }]
      : []),
    ...(l.backupId && seen.has(l.backupId) && seen.has(l.pointId)
      ? [{ from: l.backupId, to: l.pointId, backup: true }]
      : []),
  ]);
  return {
    placed,
    rows: next,
    depth: deepest,
    edges,
    unassigned: links
      .filter((l) => l.unassigned)
      .map((l) => l.pointId)
      .sort(order),
  };
}
